import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/api/client";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Loader2, ShieldAlert, ShieldCheck } from "lucide-react";

// Phase 4.5 Part 5 (extended) — the only UI surface that may change
// protected ClientFinancialProfile fields: the four Finance-attested
// exposure inputs (knownOutstandingAR/knownOverdueAR/knownOpenJobExposure/
// knownUnbilledExposure) PLUS the Finance-review fields (turnover, credit
// limits, trade history, payment behaviour, credit risk, recommendation,
// notes). Calls the SAME protected backend endpoint the rest of the app
// already uses (`PATCH /finance-gate/clients/{cid}/financial-profile`) — no
// frontend-only calculation, no shortcut write path, no duplicate record.
const NUMBER_FIELDS = [
  { key: "annualTurnover", label: "Annual turnover", help: "Customer's total annual trading turnover." },
  { key: "averageMonthlyRevenue", label: "Average monthly revenue", help: "Average monthly business volume with us." },
  { key: "approvedCreditLimit", label: "Recommended / approved credit limit", help: "The limit that governs the Finance Gate credit check once approved." },
  { key: "requestedCreditLimit", label: "Requested credit limit", help: "Limit requested by the customer/Sales." },
  { key: "recommendedCreditLimit", label: "Recommended credit limit", help: "Finance's own recommendation, prior to approval." },
  { key: "paymentTermsDays", label: "Payment terms (days)", help: "Standard credit terms, in days. Leave blank for a cash account." },
  { key: "previousTradeCount", label: "Previous trade count", help: "Number of prior completed shipments/jobs." },
  { key: "totalHistoricalBusiness", label: "Total historical business", help: "Cumulative trade value with this customer to date." },
];

const EXPOSURE_FIELDS = [
  { key: "knownOutstandingAR", label: "Current outstanding (known outstanding AR)", help: "Total invoiced but not yet collected, per your records." },
  { key: "knownOverdueAR", label: "Known overdue AR", help: "Portion of outstanding AR that is past due." },
  { key: "knownOpenJobExposure", label: "Known open job exposure (legacy)", help: "Value of jobs in progress from before this system tracked jobs automatically." },
  { key: "knownUnbilledExposure", label: "Known unbilled exposure", help: "Completed work not yet invoiced." },
];

const PAYMENT_BEHAVIOURS = ["EXCELLENT", "GOOD", "AVERAGE", "POOR"];
const CREDIT_RISKS = ["LOW", "MEDIUM", "HIGH"];

const ALL_NUMBER_KEYS = [...NUMBER_FIELDS.map((f) => f.key), ...EXPOSURE_FIELDS.map((f) => f.key)];

export function AttestDialog({ open, onOpenChange, clientId, profile, onDone }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const textKeys = ["previousPaymentBehaviour", "creditRisk", "financeRecommendation", "notes"];

  useEffect(() => {
    if (open && profile) {
      const init = {};
      for (const k of ALL_NUMBER_KEYS) init[k] = profile[k] ?? "";
      for (const k of textKeys) init[k] = profile[k] ?? "";
      setValues(init);
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profile]);

  // profile can briefly be undefined right after GET/get-or-create resolves
  // (React Query loading state) — never silently render nothing once open,
  // show a real loading state instead so "Attest / update financial data"
  // is never a dead click.
  if (!open) return null;
  if (!profile) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg" data-testid="attest-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading financial profile...
            </DialogTitle>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const validate = () => {
    const errs = {};
    for (const k of ALL_NUMBER_KEYS) {
      const v = values[k];
      if (v === "" || v === null || v === undefined) continue;
      const n = Number(v);
      if (Number.isNaN(n)) errs[k] = "Must be a number.";
      else if (n < 0) errs[k] = "Cannot be negative.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildChanges = () => {
    const changes = {};
    for (const k of ALL_NUMBER_KEYS) {
      const v = values[k];
      if (v === "" || v === null || v === undefined) continue;
      changes[k] = Number(v);
    }
    for (const k of textKeys) {
      const v = values[k];
      if (v === "" || v === null || v === undefined) continue;
      changes[k] = v;
    }
    return changes;
  };

  const save = async (attest) => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields before saving.");
      return;
    }
    setSaving(true);
    try {
      const changes = buildChanges();
      await api.patch(`/finance-gate/clients/${clientId}/financial-profile`, {
        changes, expectedVersion: profile.version, attest,
      });
      toast.success(attest ? "Financial data attested — audit trail recorded" : "Financial data saved");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, attest ? "Could not attest financial data" : "Could not save financial data"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="attest-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-600" /> Attest / update financial data
          </DialogTitle>
        </DialogHeader>
        {profile.attestedBy && profile.attestedAt && (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800">
            <ShieldCheck className="h-3.5 w-3.5" /> Currently ATTESTED by {profile.attestedBy} · {new Date(profile.attestedAt).toLocaleString()}
          </div>
        )}
        <div className="text-[12px] text-slate-500 -mt-1">
          Exposure figures below are Finance-entered because no live AR ledger exists yet. Each is valid for 7 days,
          then must be re-attested before Finance can release an order for this client.
        </div>

        <div className="space-y-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Credit &amp; turnover</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {NUMBER_FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{f.label}</Label>
                <Input type="number" min="0" value={values[f.key] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  placeholder="0" data-testid={`attest-${f.key}`} />
                {errors[f.key] && <div className="text-[11px] text-red-600 mt-0.5">{errors[f.key]}</div>}
                <div className="text-[11px] text-slate-400 mt-0.5">{f.help}</div>
              </div>
            ))}
          </div>

          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 pt-2">Finance-attested exposure inputs</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {EXPOSURE_FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{f.label}</Label>
                <Input type="number" min="0" value={values[f.key] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  placeholder="0" data-testid={`attest-${f.key}`} />
                {errors[f.key] && <div className="text-[11px] text-red-600 mt-0.5">{errors[f.key]}</div>}
                <div className="text-[11px] text-slate-400 mt-0.5">{f.help}</div>
              </div>
            ))}
          </div>

          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 pt-2">Assessment</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Previous payment behaviour</Label>
              <select className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                value={values.previousPaymentBehaviour ?? ""} onChange={(e) => setValues((v) => ({ ...v, previousPaymentBehaviour: e.target.value }))}
                data-testid="attest-previousPaymentBehaviour">
                <option value="">— Select —</option>
                {PAYMENT_BEHAVIOURS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Credit risk</Label>
              <select className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                value={values.creditRisk ?? ""} onChange={(e) => setValues((v) => ({ ...v, creditRisk: e.target.value }))}
                data-testid="attest-creditRisk">
                <option value="">— Select —</option>
                {CREDIT_RISKS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Finance recommendation</Label>
            <Textarea rows={2} value={values.financeRecommendation ?? ""} onChange={(e) => setValues((v) => ({ ...v, financeRecommendation: e.target.value }))}
              placeholder="Recommendation to management / rationale for this credit standing" data-testid="attest-financeRecommendation" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Notes</Label>
            <Textarea rows={2} value={values.notes ?? ""} onChange={(e) => setValues((v) => ({ ...v, notes: e.target.value }))}
              placeholder="Any additional context for this review" data-testid="attest-notes" />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="attest-save-draft">
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Save (no attestation)
          </Button>
          <Button onClick={() => save(true)} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="attest-save">
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Save &amp; attest
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
